import { useState } from "react";
import { toast } from "sonner";
import { UserPlus, Check, X, Trash2, Phone, MapPin, IdCard } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { AdminTitle } from "@/admin/AdminLayout";
import { FilterBar } from "@/admin/Decision";
import { StatusBadge } from "@/components/Brand";
import { PinInput } from "@/components/PinInput";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, useApi } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";

const ApproveDialog = ({ reg, onClose, onDone }) => {
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const approve = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/registrations/${reg.reg_id}/approve`, { pin });
      toast.success(`${reg.name} resmi menjadi anggota. Berikan PIN awal kepada anggota.`);
      onDone();
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-sm" data-testid="approve-registration-dialog">
        <DialogHeader>
          <DialogTitle>Setujui {reg.name}</DialogTitle>
          <DialogDescription>Buat PIN awal 6 angka. Anggota wajib menggantinya saat login pertama.</DialogDescription>
        </DialogHeader>
        <PinInput value={pin} onChange={setPin} testid="approve-registration-pin" />
        <button onClick={approve} disabled={busy || pin.length !== 6} data-testid="approve-registration-submit"
          className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-semibold py-3 rounded-xl">Setujui & Buat Akun</button>
      </DialogContent>
    </Dialog>
  );
};

const RegCard = ({ r, isSuper, onApprove, onDone }) => {
  const [note, setNote] = useState("");
  const act = async (fn, msg) => {
    try {
      await fn();
      toast.success(msg);
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm hover:shadow-md transition-shadow fade-up" data-testid={`registration-${r.reg_id}`}>
      <div className="flex items-start gap-4">
        <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-violet-600 to-fuchsia-400 text-white flex items-center justify-center font-bold shrink-0">
          {r.name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap"><b className="text-slate-900">{r.name}</b><StatusBadge status={r.status} /></div>
          <div className="text-xs text-slate-500">{r.email} • {fmtDateTime(r.created_at)}</div>
          <div className="grid sm:grid-cols-3 gap-2 mt-3 text-xs text-slate-700">
            <span className="flex items-center gap-1.5"><IdCard className="h-3.5 w-3.5 text-blue-600" />{r.nik}</span>
            <span className="flex items-center gap-1.5"><Phone className="h-3.5 w-3.5 text-emerald-600" />{r.phone}</span>
            <span className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5 text-rose-600" />{r.address}</span>
          </div>
          {r.note && <div className="text-xs text-slate-500 mt-2">Catatan: {r.note}</div>}
          {r.admin_note && <div className="text-xs text-slate-700 mt-1">Alasan: {r.admin_note}</div>}
        </div>
      </div>
      <div className="flex flex-wrap gap-2 mt-4 justify-end items-center">
        {r.status === "pending" && (
          <>
            {isSuper && <Input data-testid={`reject-note-${r.reg_id}`} className="h-9 w-48" placeholder="Alasan penolakan" value={note} onChange={(e) => setNote(e.target.value)} />}
            {isSuper && (
              <button onClick={() => act(() => api.post(`/admin/registrations/${r.reg_id}/reject`, { note: note || null }), "Pendaftaran ditolak")}
                data-testid={`reject-registration-${r.reg_id}`} className="h-9 px-3 rounded-lg border border-red-300 text-red-600 hover:bg-red-50 text-xs font-bold flex items-center gap-1"><X className="h-4 w-4" />Tolak</button>
            )}
            <button onClick={() => onApprove(r)} data-testid={`approve-registration-${r.reg_id}`} className="h-9 px-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold flex items-center gap-1"><Check className="h-4 w-4" />Setujui</button>
          </>
        )}
        {isSuper && (
          <button onClick={() => window.confirm(`Hapus pendaftaran ${r.name}?`) && act(() => api.delete(`/admin/registrations/${r.reg_id}`), "Pendaftaran dihapus")}
            data-testid={`delete-registration-${r.reg_id}`} className="h-9 px-3 rounded-lg bg-slate-100 hover:bg-red-100 text-slate-600 hover:text-red-700 text-xs font-bold flex items-center gap-1"><Trash2 className="h-4 w-4" />Hapus</button>
        )}
      </div>
    </div>
  );
};

export default function AdminRegistrations() {
  const { user } = useAuth();
  const { data, reload } = useApi("/admin/registrations");
  const [filter, setFilter] = useState("pending");
  const [approving, setApproving] = useState(null);
  const list = (data || []).filter((r) => filter === "all" || r.status === filter);
  return (
    <div>
      <AdminTitle icon={UserPlus} title="Pendaftaran Anggota" sub="Calon anggota yang mencoba masuk dengan email belum terdaftar" />
      <FilterBar value={filter} onChange={setFilter} />
      <div className="grid xl:grid-cols-2 gap-4" data-testid="registrations-list">
        {list.length === 0 && <p className="text-sm text-slate-500" data-testid="registrations-empty">Tidak ada pendaftaran.</p>}
        {list.map((r) => <RegCard key={r.reg_id} r={r} isSuper={user.role === "superadmin"} onApprove={setApproving} onDone={reload} />)}
      </div>
      {approving && <ApproveDialog reg={approving} onClose={() => setApproving(null)} onDone={reload} />}
    </div>
  );
}
